import { Locale } from '@headless-workspace/config';
import { AuthorizationStrategy, QueryParamStrategy, RefreshTokenStrategy } from '@headless-workspace/data';
import { CacheService, CacheOptions, IsomorphicLogger } from '@headless-workspace/utils';
import { Repository } from './Repository';
import { Result } from './Result';

const logger = new IsomorphicLogger();

const LOGGER_LABEL = 'LoggerCachedRepository';

export abstract class CachedRepository extends Repository {
    protected cacheService?: CacheService;

    protected constructor(
        authStrategy: Partial<AuthorizationStrategy>,
        refreshTokenStrategy: Partial<RefreshTokenStrategy>,
        queryParamStrategy: Partial<QueryParamStrategy>,
    ) {
        super(authStrategy, refreshTokenStrategy, queryParamStrategy);
        this.cacheService = new CacheService();
    }

    protected async withCache<T>(
        locale: Locale,
        prefix: string,
        cacheKey: Record<string, any>,
        options: CacheOptions,
        fetchFn: () => Promise<Result<T>>,
    ): Promise<Result<T>> {
        if (!this.cacheService) {
            return fetchFn();
        }
        try {
            const { data: cached, isStale } = await this.cacheService.get<Result<T>>(locale, prefix, cacheKey);

            if (cached) {
                if (!isStale) {
                    return cached;
                }
                // Return stale data and revalidate in background
                this.backgroundRevalidate(locale, prefix, cacheKey, options, fetchFn);
                return cached;
            }
            // Cache miss - fetch fresh data
            const result = await fetchFn();
            if (result.type === 'success') {
                await this.cacheService.set(locale, prefix, cacheKey, result, options);
            }
            return result;
        } catch (error) {
            logger.error(LOGGER_LABEL, `Cache operation failed: ${error}`);
            return fetchFn();
        }
    }

    private async backgroundRevalidate<T>(
        locale: Locale,
        prefix: string,
        cacheKey: Record<string, any>,
        options: CacheOptions,
        fetchFn: () => Promise<Result<T>>,
    ): Promise<void> {
        try {
            const result = await fetchFn();
            if (result.type === 'success' && this.cacheService) {
                await this.cacheService.set(locale, prefix, cacheKey, result, options);
            }
        } catch (error) {
            logger.error(LOGGER_LABEL, `Background revalidation failed: ${error}`);
        }
    }

    protected async invalidateCache(locale: Locale, tags: string[]): Promise<void> {
        if (this.cacheService) {
            await this.cacheService.invalidateByTags(locale, tags);
        }
    }
}
